import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Card, Divider, Stack, useMediaQuery, useTheme } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

import {
  getChatState,
  getConversations,
  getMessages,
  selectConversation,
} from '../../../store/V2/ChatSlice';
import { useAppSelector } from '../../../hooks';
import { getCoursesState } from '../../../store/V2/CoursesSlice';
import { IDENTIFIED_USER_ROLES } from '../../../store/UserSlice';
import { getCourseStudentsByCourseId } from '../../../store/V2/CourseStudentsSlice/api';
import { TP_COLORS } from '../../../constants';
import { TPTextField } from '../../../components/Inputs';
import ConversationsList from './components/ConversationsList';
import ChatHeader from './components/ChatHeader';
import NewConversation from './components/NewConversation';
import Conversation from './components/Conversation';

interface ChatBoxProps {
  isChatOpen: boolean;
  onChatCollapse: () => void;
}

const ChatBox: React.FC<ChatBoxProps> = ({ isChatOpen, onChatCollapse }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const urlParams = useParams();
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  // Store
  const { userCourse: courseData } = useAppSelector(getCoursesState);
  const { isNewChat, selectedConversation, conversations } = useAppSelector(
    getChatState
  );

  // State
  const [searchText, setSearchText] = useState('');

  const questyStringParams = new URLSearchParams(location.search);
  const courseId = urlParams.id;
  const urlConversationId = questyStringParams.get('conversation') || '';
  const chatWidth = isXsScreen ? '99%' : '350px';

  useEffect(() => {
    if (courseId) {
      dispatch(
        getConversations({
          course: courseId,
        })
      );

      if (
        courseData?.identifiedCourseUserRole === IDENTIFIED_USER_ROLES.TUTOR
      ) {
        dispatch(getCourseStudentsByCourseId(courseId));
      }
    }
  }, []);

  useEffect(() => {
    if (selectedConversation?._id) {
      dispatch(
        getMessages({
          conversation: selectedConversation._id,
          limit: 200,
          offset: 0,
        })
      );
      setSearchText('');
    }
  }, [selectedConversation?._id]);

  useEffect(() => {
    if (conversations && conversations.length && urlConversationId) {
      const _matchingConversation = conversations.find(
        (item) => item._id === urlConversationId
      );

      if (_matchingConversation) {
        dispatch(selectConversation(_matchingConversation));
      }
    }
  }, [urlConversationId]);

  useEffect(() => {
    setSearchText('');
  }, [isNewChat]);

  const renderBody = () => {
    if (selectedConversation && !isNewChat) {
      return <Conversation />;
    }

    if (isNewChat) {
      return <NewConversation searchText={searchText} />;
    }

    return <ConversationsList searchText={searchText} />;
  };

  return (
    <Card
      elevation={0}
      variant={isXsScreen ? undefined : 'outlined'}
      style={{
        width: isChatOpen ? chatWidth : '40px',
        maxWidth: '100%',
        display: isChatOpen ? 'flex' : 'none',
        flexDirection: 'column',
        borderRadius: '20px',
        height: isXsScreen ? 'calc(100vh - 15px)' : 'calc(100vh - 250px)',
        minHeight: '500px',
        border: `1px solid ${isXsScreen ? 'transparent' : '#E2E8F0'}`,
      }}
    >
      {/* Header Section */}
      <ChatHeader onChatCollapse={onChatCollapse} />

      {!selectedConversation ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            backgroundColor: TP_COLORS.listItemGray.light,
            borderRadius: '28px',
            padding: '8px',
            margin: '8px',
          }}
        >
          <SearchIcon
            fontSize="small"
            sx={{ padding: '5px', color: TP_COLORS.gray.darken }}
          />

          <TPTextField
            multiline
            fullWidth
            placeholder="Search"
            variant="standard"
            value={searchText}
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: '14px',
              },
            }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearchText(e.target.value);
            }}
          />
        </Stack>
      ) : (
        <Divider />
      )}

      {renderBody()}
    </Card>
  );
};

export default ChatBox;
